import { STORAGE_KEYS } from "@watchover/shared";
import {
  getCookie,
  setCookie,
  getRegistrableDomain,
} from "./cookies.js";

/**
 * Identity + session id management.
 *
 * Stored in cookies scoped to the registrable root domain so a visitor and
 * their session are shared across subdomains (jetvisa.io ↔ app.jetvisa.io ↔
 * checkout.jetvisa.io), giving one continuous journey. Falls back to
 * localStorage when cookies are unavailable (which loses cross-subdomain
 * continuity but keeps per-origin persistence).
 */

// Resolved once via initStorage(). undefined => host-scoped (no subdomain share).
let cookieDomain: string | undefined;
// Per-project namespace so two WatchOver projects on the same host/domain don't
// share visitor/session identity (their keys differ → different cookies).
let ns = "";

/** Namespaced storage key, e.g. "wo_ses" -> "wo_ses_a1b2c3". */
export function storageKey(base: string): string {
  return ns ? `${base}_${ns}` : base;
}

/** Tiny stable hash (djb2) of the project key, base36. */
function shortHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function initStorage(opts: {
  crossSubdomain: boolean;
  cookieDomain?: string;
  projectKey: string;
}): string | undefined {
  ns = shortHash(opts.projectKey);
  if (opts.crossSubdomain) {
    cookieDomain = opts.cookieDomain || getRegistrableDomain() || undefined;
  } else {
    cookieDomain = undefined;
  }
  return cookieDomain;
}

function read(key: string): string | null {
  const k = storageKey(key);
  const fromCookie = getCookie(k);
  if (fromCookie !== null) return fromCookie;
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

function write(key: string, val: string, days = 730): void {
  const k = storageKey(key);
  setCookie(k, val, { domain: cookieDomain, days });
  // Mirror to localStorage so we can still read if cookies get cleared/blocked.
  try {
    localStorage.setItem(k, val);
  } catch {
    /* ignore */
  }
}

function rid(prefix: string): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = (Math.random() * 256) | 0;
  }
  let out = "";
  for (const b of bytes) out += b.toString(36).padStart(2, "0");
  return `${prefix}_${out.slice(0, 24)}`;
}

export function getVisitorId(): string {
  let id = read(STORAGE_KEYS.visitorId);
  if (!id) {
    id = rid("vis");
    write(STORAGE_KEYS.visitorId, id);
  }
  return id;
}

interface StoredSession {
  id: string;
  last: number; // last-activity epoch ms
}

/**
 * Current session id, shared across subdomains via the root-domain cookie.
 * A fresh id is minted when none exists or the previous one has been idle past
 * the timeout. `touch()` keeps it alive and rotates it after inactivity.
 */
export class SessionTracker {
  private id: string;
  private last: number;
  private isNew = false;

  constructor(private timeoutMs: number) {
    const now = Date.now();
    const raw = read(STORAGE_KEYS.session);
    let stored: StoredSession | null = null;
    if (raw) {
      try {
        stored = JSON.parse(raw) as StoredSession;
      } catch {
        stored = null;
      }
    }
    if (stored && now - stored.last < timeoutMs) {
      this.id = stored.id;
      this.last = stored.last;
    } else {
      this.id = rid("ses");
      this.last = now;
      this.isNew = true;
    }
    this.persist();
  }

  get sessionId(): string {
    return this.id;
  }

  get startedNew(): boolean {
    return this.isNew;
  }

  touch(): { rotated: boolean } {
    const now = Date.now();
    let rotated = false;
    if (now - this.last >= this.timeoutMs) {
      this.id = rid("ses");
      rotated = true;
      this.isNew = true;
    }
    this.last = now;
    this.persist();
    return { rotated };
  }

  private persist() {
    // Short-lived cookie; the `last` timestamp drives timeout, not the cookie.
    write(STORAGE_KEYS.session, JSON.stringify({ id: this.id, last: this.last }), 1);
  }
}

export function getStoredUserId(): string | null {
  return read(STORAGE_KEYS.userId);
}
export function setStoredUserId(id: string) {
  write(STORAGE_KEYS.userId, id);
}
